import React, { useState } from 'react';
import { Loader2, Search, Eye, X } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { cn } from '../../lib/utils';
import {
  slidesBotApi,
  SlidesJobStatus,
  AdminJobRow,
  JobDetail,
} from '../../api/slidesBot';
import { PanelCard, useFetchOnce } from './index';

interface Props {
  isDark: boolean;
}

const STATUSES: SlidesJobStatus[] = [
  'completed',
  'failed',
  'cancelled',
  'generating',
  'plan_ready',
  'awaiting_topic',
  'awaiting_meta',
  'generating_plan',
  'approved',
  'draft',
];

const STATUS_VARIANT: Partial<Record<SlidesJobStatus, 'default' | 'destructive' | 'secondary' | 'outline'>> = {
  completed: 'default',
  failed: 'destructive',
  cancelled: 'outline',
  generating: 'secondary',
  plan_ready: 'secondary',
};

const fmtDate = (s: string | null) => {
  if (!s) return '—';
  const d = new Date(s);
  return (
    d.toLocaleDateString('ru-RU') +
    ' ' +
    d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
  );
};

export default function JobsView({ isDark }: Props) {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<string>('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [openJob, setOpenJob] = useState<AdminJobRow | null>(null);
  const limit = 20;

  const { data, loading, error } = useFetchOnce(
    () =>
      slidesBotApi.listJobs({
        page,
        limit,
        status: status || undefined,
        search: search || undefined,
      }),
    [page, status, search],
  );

  const totalPages = data ? Math.ceil(data.total / limit) : 1;

  return (
    <div className="grid gap-4">
      <PanelCard isDark={isDark}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
            setSearch(searchInput.trim());
          }}
          className="flex gap-2"
        >
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by topic..."
              className="pl-9"
            />
          </div>
          <Select
            value={status || 'all'}
            onValueChange={(v) => {
              setStatus(v === 'all' ? '' : v);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-44">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button type="submit">Search</Button>
        </form>
      </PanelCard>

      <PanelCard isDark={isDark} className="overflow-x-auto p-0">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-[#0EA5A4]" />
          </div>
        ) : error ? (
          <p className="p-5 text-red-500">Failed to load: {error}</p>
        ) : !data || data.items.length === 0 ? (
          <p
            className={cn(
              'p-8 text-center',
              isDark ? 'text-slate-500' : 'text-slate-400',
            )}
          >
            No jobs match the filters
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Topic</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Slides</TableHead>
                <TableHead className="text-right">Cost</TableHead>
                <TableHead>Created</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((j) => (
                <TableRow key={j.id}>
                  <TableCell>
                    <div className="font-medium truncate max-w-xs">
                      {j.topic ?? <span className="italic">—</span>}
                    </div>
                    {j.subject && (
                      <div className="text-xs text-slate-500">{j.subject}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {j.username ?? '—'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={STATUS_VARIANT[j.status] ?? 'outline'}>
                      {j.status}
                    </Badge>
                    {j.error_code && (
                      <div className="text-xs text-red-400 mt-1">
                        {j.error_code}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {j.slide_count ?? '—'}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {j.cost_usd && Number(j.cost_usd) > 0
                      ? `$${Number(j.cost_usd).toFixed(4)}`
                      : '—'}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">
                    {fmtDate(j.created_at)}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setOpenJob(j)}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </PanelCard>

      {data && data.total > limit && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">
            {data.total} jobs · page {page} of {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              ← Prev
            </Button>
            <Button
              variant="outline"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next →
            </Button>
          </div>
        </div>
      )}

      {openJob && (
        <JobDetailDialog
          jobId={openJob.id}
          onClose={() => setOpenJob(null)}
        />
      )}
    </div>
  );
}

function JobDetailDialog({ jobId, onClose }: { jobId: string; onClose: () => void }) {
  const { data, loading, error } = useFetchOnce<JobDetail>(
    () => slidesBotApi.getJob(jobId),
    [jobId],
  );

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Job details
            <button onClick={onClose} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-[#0EA5A4]" />
          </div>
        )}
        {error && <p className="text-red-500">{error}</p>}
        {data && (
          <div className="space-y-4">
            <div className="text-sm space-y-1">
              <p>
                <span className="font-medium">Topic:</span> {data.job.topic}
              </p>
              <p>
                <span className="font-medium">User:</span>{' '}
                {data.job.username ?? data.job.user_id}
              </p>
              <p>
                <span className="font-medium">Type / Level / Lang:</span>{' '}
                {data.job.type} · {data.job.level} · {data.job.language}
                {data.job.subject && ` · ${data.job.subject}`}
              </p>
              <p>
                <span className="font-medium">Status:</span> {data.job.status}
              </p>
              <p>
                <span className="font-medium">Tokens:</span>{' '}
                {data.job.tokens_input} in / {data.job.tokens_output} out · cost{' '}
                ${Number(data.job.cost_usd).toFixed(4)}
              </p>
              {data.job.result_pptx_url && (
                <p>
                  <a
                    href={data.job.result_pptx_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#0EA5A4] underline"
                  >
                    Download PPTX
                  </a>
                </p>
              )}
              {data.job.error_message && (
                <div className="rounded bg-red-500/10 p-3 text-sm text-red-500">
                  <p className="font-medium">{data.job.error_code}</p>
                  <p>{data.job.error_message}</p>
                </div>
              )}
            </div>

            {data.slides.length > 0 && (
              <div>
                <h3 className="font-semibold mb-2">Slides</h3>
                <ol className="space-y-2 text-sm">
                  {data.slides.map((s) => (
                    <li key={s.id} className="border-l-2 border-slate-700 pl-3">
                      <p className="font-medium">
                        {s.position}. {s.title}
                      </p>
                      <p className="text-xs text-slate-500">
                        intent: {s.intent ?? '—'}
                      </p>
                      {s.bullets && s.bullets.length > 0 && (
                        <ul className="mt-1 ml-4 list-disc text-xs text-slate-400">
                          {s.bullets.map((b, i) => (
                            <li key={i}>{b}</li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
